import { APP_BASE_HREF } from "@angular/common";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import { ConfigServiceCommand } from "@fabric-types/svc-config";
import { Store, select } from "@ngrx/store";
import { LoginConsentsContainer } from "@obg/common.login";
import { OPEN_ID_CONNECT_DIALOG_ID, OpenIdConnectContainer } from "@obg/common.verification";
import { LOGIN_CONSENTS_DIALOG_ID } from "@obg/mat.ui.common";
import { COOKIE_SETTINGS_DIALOG_ID } from "@obg/mat.ui.common/cookie-settings-dialog";
import { CUSTOMER_VERIFICATION_DIALOG_ID } from "@obg/mat.ui.common/customer-verification";
import { DialogService } from "@obg/mat.ui.core";
import { batchActions, RehydrateAction } from "@obg/ngrx-utils";
import { AffiliateService, CommonContentAction, DataProtectionAction, FraudPreventionService } from "@obg/ngx.common";
import {
	AppContextConfigService,
	AppContextSelector,
	AuthAction,
	CORE_ROUTE,
	CoreHttpClient,
	CurrencyAction,
	Jurisdiction,
	JurisdictionSelector,
	LimitsReminderAction,
	MarketAction,
	ProductType,
	SessionAction,
	SessionEndReason,
	STARTUP_CONTEXT_TOKEN,
	StartupContext,
	CustomerManagementSelector,
} from "@obg/ngx.core";
import {
	CoreContentState,
	FlagAction,
	IconAction,
	ImageAction,
	MenuAction,
	TranslationAction,
} from "@obg/ngx.core.content";
import { CacheService } from "@obg/ngx.global";
import { CookieService, DocumentRef, PlatformService } from "@obg/ngx.global.evo";
import { addHours } from "date-fns";
import { merge, Subscription } from "rxjs";
import { filter, take, tap } from "rxjs/operators";

@Injectable({
	providedIn: "root",
})
export class AppBootstrapper implements OnDestroy {
	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreContentState>,
		private rehydrateAction: RehydrateAction,
		private sessionAction: SessionAction,
		private appContextConfig: AppContextConfigService,
		private currencyAction: CurrencyAction,
		private coreHttpClient: CoreHttpClient,
		private dialogService: DialogService,
		private dataProtectionAction: DataProtectionAction,
		private cookieService: CookieService,
		private commonContentAction: CommonContentAction,
		private cacheService: CacheService,
		private affiliateService: AffiliateService,
		private fraudPreventionService: FraudPreventionService,
		private platformService: PlatformService,
		private authAction: AuthAction,
		private marketAction: MarketAction,
		private documentRef: DocumentRef,
		private flagAction: FlagAction,
		@Inject(APP_BASE_HREF) private appBaseHref: string,
		@Inject(STARTUP_CONTEXT_TOKEN) private startupContext: StartupContext
	) {}

	/**
	 * This should be called once.
	 */
	init() {
		const { countryCode } = this.startupContext.appContext;
		// TODO: OBGB-12018 to remove bang
		this.coreHttpClient.setDefaultHeader("X-OBG-Device", this.startupContext.device.deviceType);
		this.coreHttpClient.setDefaultHeader("X-OBG-Channel", this.startupContext.device.deviceExperience);

		if (countryCode) {
			this.coreHttpClient.setDefaultHeader("X-OBG-Country-Code", countryCode);
		}

		const jurisdictionDispatcher$ = this.store.pipe(
			filter(() => !this.platformService.isServer),
			select(JurisdictionSelector.get),
			tap((jurisdiction) => {
				if (!jurisdiction) {
					return;
				}
				this.documentRef.obgEventBus?.dispatch(ConfigServiceCommand.UpdateJurisdiction, { jurisdiction });
			})
		);

		// eslint-disable-next-line rxjs/no-ignored-subscription
		this.cacheService.purge().subscribe();

		this.affiliateService.initialize();

		this.fraudPreventionService.init();

		this.store.dispatch(
			batchActions(
				...[
					this.rehydrateAction.rehydrate(),
					this.currencyAction.initialize(),
					TranslationAction.init(this.appBaseHref),
					...(!globalThis.isDOMSSR && this.platformService.isBrowser
						? [this.sessionAction.verify(this.appContextConfig.getSessionToken())]
						: [this.authAction.sessionEnd(SessionEndReason.VerificationFailed)]),
					MenuAction.getAllByName(["desktop-common-menus", "desktop-casino-menus", "desktop-mega-menu"]),
					IconAction.getAll(),
					this.flagAction.getAll(),
					TranslationAction.get(["general", "not-supported"]),
					ImageAction.getAll("general"),
					this.dataProtectionAction.getGdprEnabledCountries(),
					this.commonContentAction.getCommonMainBanners(ProductType.Common),
					LimitsReminderAction.initializeLimitsReminderType(),
					this.marketAction.getSuccess({
						currentMarket: this.startupContext.market,
						markets: this.startupContext.marketList,
					}),
				]
			)
		);

		const product$ = this.store.select(AppContextSelector.getProduct).pipe(
			filter((product) => product === ProductType.Common || product === ProductType.ProviderPlugin),
			take(1),
			tap(() =>
				this.store.dispatch(
					batchActions(TranslationAction.get([ProductType.Common]), ImageAction.getAll(ProductType.Common))
				)
			)
		);

		const customerId$ = this.platformService.forClient(() =>
			this.store.pipe(
				select(CustomerManagementSelector.getCustomerId),
				tap((customerId) => {
					if (!customerId) {
						typeof localStorage !== "undefined" ? localStorage.removeItem("OptiGUID") : undefined;
						typeof sessionStorage !== "undefined" ? sessionStorage.removeItem("ObgCustomerId") : undefined;
						return;
					}

					typeof localStorage !== "undefined" ? localStorage.setItem("OptiGUID", customerId) : undefined;
					typeof sessionStorage !== "undefined"
						? sessionStorage.setItem("ObgCustomerId", customerId)
						: undefined;
				})
			)
		);

		this.data$$ = merge(customerId$, product$, jurisdictionDispatcher$).subscribe();

		this.initDialogs();

		// TODO: remove this logic as part of desktop post release cleanup (https://jirasson.betsson.local/browse/OBGD-1853)
		this.setBetaCookie();
	}

	ngOnDestroy() {
		if (this.data$$) {
			this.data$$.unsubscribe();
		}
	}

	private setBetaCookie() {
		const name = "OBG-BETA";

		if (!this.appContextConfig.isBeta() || this.cookieService.get(name) || this.cookieService.get("OBG-TEST")) {
			return;
		}

		const expiryDate = addHours(new Date(), this.appContextConfig.getBetaCookieExpiry());
		this.cookieService.set({
			expiryDate,
			name,
			value: "true",
		});
	}

	private initDialogs() {
		const paymentConfig = {
			width: "100vw",
			minWidth: "100vw",
			panelClass: "payment-panel",
			backdropClass: "payment-backdrop",
		};

		this.dialogService.registerRouteConfig(CORE_ROUTE.reward.depositBonus, {
			panelClass: "deposit-bonus-panel",
			backdropClass: "deposit-bonus-backdrop",
			disableClose: true,
		});
		this.dialogService.registerRouteConfig(CORE_ROUTE.payment.deposit, {
			...paymentConfig,
			noDialogForRoute: [CORE_ROUTE.payment.withdraw],
		});
		this.dialogService.registerRouteConfig(CORE_ROUTE.payment.withdraw, {
			...paymentConfig,
			noDialogForRoute: [CORE_ROUTE.payment.deposit],
		});
		this.dialogService.registerRouteConfig(CORE_ROUTE.cookieSettings, {
			id: COOKIE_SETTINGS_DIALOG_ID,
			width: "860px",
			autoFocus: false,
			disableClose: true,
		});
		this.dialogService.registerRouteConfig(CORE_ROUTE.registration.registerSuccess, {
			id: CUSTOMER_VERIFICATION_DIALOG_ID,
			width: "580px",
		});

		this.dialogService.registerRouteConfig(CORE_ROUTE.auth.login, {
			minHeight: "400px",
			// we do not want autofocus for DGA brands on the ID select login modal
			// TODO: AOCC-4830 enable autofocus when NemID is removed
			autoFocus: this.startupContext.market.jurisdiction === Jurisdiction.Dga.toUpperCase() ? false : true,
		});

		this.dialogService.registerRouteConfig(CORE_ROUTE.auth.bankIdVerification, {
			minHeight: "400px",
		});

		this.dialogService.registerRouteConfig(CORE_ROUTE.responsibleGaming.depositLimitDialog, {
			width: "580px",
			autoFocus: false,
		});

		this.dialogService.registerComponentConfig(LoginConsentsContainer, {
			width: "800px",
			id: LOGIN_CONSENTS_DIALOG_ID,
			autoFocus: false,
			disableClose: true,
			maxHeight: "800px",
		});

		this.dialogService.registerRouteConfig(CORE_ROUTE.responsibleGaming.selectPlayerProfile, {
			width: "580px",
		});

		this.dialogService.registerComponentConfig(OpenIdConnectContainer, {
			width: "800px",
			id: OPEN_ID_CONNECT_DIALOG_ID,
			autoFocus: false,
			maxHeight: "800px",
			disableClose: true,
		});

		this.dialogService.registerRouteConfig(CORE_ROUTE.gameRoundHistory, {
			width: "700px",
		});
	}
}
